import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useToast } from '@chakra-ui/react';
import { ApolloError } from '@apollo/client';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { hasAnyEDRIntegrationEnabledForTenant } from '@investigator/features/integrations/pages';
import { EDRIntegration } from '@investigator/features/integrations/pages/IntegrationsConstants';
import {
  CrowdStrikeEndpointDevice,
  MicrosoftDefenderEndpointDevice,
  EdrConfigurationType,
  IdentityIncident,
  EntraSigninWithUser,
  EntityDataStoreEntityType,
} from '@investigator/shared/generated/graphql';
import {
  useGetEDREndpointDevices,
  useGetEntityEdrStatus,
  usePullEntityEdrStatus,
  usePerformEdrAction,
  useGetIncidentsForUserQuery,
  useGetEntraSigninHistory,
  useGetUserHistoryForEndpointDeviceEntityQuery,
  GET_ENTITY_EDR_STATUS,
} from '../queries';
import {
  EntityEdrContextType,
  OrganizedEDRData,
  PerformEdrActionParams,
} from '../types';
import { useEntitiesContext } from './EntitiesContext';

const { edrAction } = i18n.pages.detections.entityEDR;

const EntityEdrContext = createContext<EntityEdrContextType | undefined>(
  undefined,
);

export const EntityEdrProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  // Identity Alerts state
  const [identityAlertsLoading, setIdentityAlertsLoading] = useState(false);
  const [identityAlerts, setIdentityAlerts] = useState<
    (IdentityIncident & { userId?: string })[]
  >([]);
  const [currentUserIdIndex, setCurrentUserIdIndex] = useState(-1);
  const [identityAlertsError, setIdentityAlertsError] = useState<
    ApolloError | undefined
  >(undefined);

  const toast = useToast();
  const { userPrivileges, userLicense } = useContext(AuthContext);
  const {
    selectedEntity,
    analysisModal: { isOpen: isModalOpen },
  } = useEntitiesContext();

  const { entityName, entityType, tenantInfo, lastSeen } = selectedEntity || {};
  const { tenant_id = '' } = tenantInfo || {};

  // Use entity's tenant if available, otherwise fall back to user's license tenant
  const resolvedTenantId = tenant_id || userLicense?.tenant || '';

  // Check if any EDR integration is enabled for this tenant
  const hasAnyEDRIntegrationEnabled = useMemo(
    () =>
      hasAnyEDRIntegrationEnabledForTenant(
        resolvedTenantId,
        userPrivileges || {},
      ),
    [resolvedTenantId, userPrivileges],
  );

  const {
    data: edrEndpointData,
    loading: edrLoading,
    error: edrError,
  } = useGetEDREndpointDevices({
    skip:
      !resolvedTenantId ||
      !hasAnyEDRIntegrationEnabled ||
      !entityName ||
      !entityType,
    variables: {
      edr_endpoint_device_inputs: [
        {
          as_of: lastSeen,
          tenant: resolvedTenantId,
          alert_entity_info: {
            entity_name: entityName,
            entity_type: entityType,
          },
        },
      ],
    },
  });

  // Process and organize EDR data by provider
  const organizedData = useMemo<OrganizedEDRData | null>(() => {
    if (!edrEndpointData?.getEdrEndpointDevices) return null;

    const processedEDRData: Partial<
      Record<
        EDRIntegration,
        CrowdStrikeEndpointDevice | MicrosoftDefenderEndpointDevice
      >
    > = {};

    for (const integration of edrEndpointData.getEdrEndpointDevices
      .edr_endpoint_devices) {
      const ds = integration?.data_source;

      // IMPORTANT:
      // ds is GraphQL enum (EdrConfigurationType) and may include microsoft_entra_id.
      // Only assign when it's one of our current UI integrations.
      if (
        ds === EDRIntegration.CROWDSTRIKE ||
        ds === EDRIntegration.MICROSOFT_DEFENDER ||
        ds === EDRIntegration.MICROSOFT_ENTRA_ID
      ) {
        processedEDRData[ds] = integration as unknown as
          | CrowdStrikeEndpointDevice
          | MicrosoftDefenderEndpointDevice;
      }
    }

    return processedEDRData as OrganizedEDRData;
  }, [edrEndpointData]);

  const crowdstrikeEntityId = organizedData?.crowdstrike?.entity_id ?? '';

  const {
    data: crowdstrikeStatusData,
    loading: crowdstrikeStatusLoading,
    error: crowdstrikeStatusError,
  } = useGetEntityEdrStatus({
    fetchPolicy: 'cache-first',
    skip: !isModalOpen || !resolvedTenantId || !crowdstrikeEntityId,
    variables: {
      tenant: resolvedTenantId,
      entity_id: crowdstrikeEntityId,
    },
  });

  const [pullCrowdstrikeStatus, { loading: pullCrowdstrikeStatusLoading }] =
    usePullEntityEdrStatus({
      fetchPolicy: 'network-only',
      variables: {
        tenant: resolvedTenantId,
        entity_id: crowdstrikeEntityId,
      },
      refetchQueries: [
        {
          query: GET_ENTITY_EDR_STATUS,
          variables: {
            tenant: resolvedTenantId,
            entity_id: crowdstrikeEntityId,
          },
          fetchPolicy: 'network-only',
        },
      ],
      awaitRefetchQueries: true,
      notifyOnNetworkStatusChange: true,
    });

  const refreshCrowdstrikeStatus = useCallback(() => {
    if (crowdstrikeEntityId) {
      pullCrowdstrikeStatus();
    }
  }, [crowdstrikeEntityId, pullCrowdstrikeStatus]);

  const defenderEntityId = organizedData?.microsoft_defender?.entity_id ?? '';

  const {
    data: defenderStatusData,
    loading: defenderStatusLoading,
    error: defenderStatusError,
  } = useGetEntityEdrStatus({
    fetchPolicy: 'cache-first',
    skip: !isModalOpen || !resolvedTenantId || !defenderEntityId,
    variables: {
      tenant: resolvedTenantId,
      entity_id: defenderEntityId,
    },
  });

  const [pullDefenderStatus, { loading: pullDefenderStatusLoading }] =
    usePullEntityEdrStatus({
      fetchPolicy: 'network-only',
      variables: {
        tenant: resolvedTenantId,
        entity_id: defenderEntityId,
      },
      refetchQueries: [
        {
          query: GET_ENTITY_EDR_STATUS,
          variables: { tenant: resolvedTenantId, entity_id: defenderEntityId },
          fetchPolicy: 'network-only',
        },
      ],
      awaitRefetchQueries: true,
      notifyOnNetworkStatusChange: true,
    });

  const refreshDefenderStatus = useCallback(() => {
    if (defenderEntityId) {
      pullDefenderStatus();
    }
  }, [defenderEntityId, pullDefenderStatus]);

  const paloAltoEntityId = `${resolvedTenantId}-palo_alto_networks-endpoint_device-${entityName}`;

  const {
    data: paloAltoStatusData,
    loading: paloAltoStatusLoading,
    error: paloAltoStatusError,
  } = useGetEntityEdrStatus({
    fetchPolicy: 'cache-first',
    skip: !isModalOpen || !resolvedTenantId || !entityName,
    variables: {
      tenant: resolvedTenantId,
      entity_id: paloAltoEntityId,
    },
  });

  // Palo Alto doesn't have a pull status, so we use the same query
  // TODO: Implement Palo Alto refresh when pullEntityEdrStatus is available for this provider
  const refreshPaloAltoStatus = useCallback(() => {
    // No-op: Palo Alto doesn't currently support pullEntityEdrStatus
  }, []);

  // Entra ID Sign-in History Logic
  const defenderDeviceId =
    organizedData?.microsoft_defender?.machine?.aadDeviceId || undefined;
  const crowdstrikeHostName =
    organizedData?.crowdstrike?.host_info?.host_name || undefined;

  // Pagination state
  const [entraHistoryPagination, setEntraHistoryPagination] = useState({
    offset: 0,
    size: 50,
  });

  const shouldSkipEntraHistoryQuery =
    !isModalOpen ||
    !resolvedTenantId ||
    !lastSeen ||
    (!defenderDeviceId && !crowdstrikeHostName);

  const {
    data: entraSigninHistoryData,
    loading: entraSigninHistoryLoading,
    error: entraSigninHistoryError,
    refetch: refetchEntraSigninHistory,
  } = useGetEntraSigninHistory({
    skip: shouldSkipEntraHistoryQuery,
    variables: {
      input: {
        tenant: resolvedTenantId,
        device_id: defenderDeviceId,
        display_name: crowdstrikeHostName,
        offset: entraHistoryPagination.offset,
        size: entraHistoryPagination.size,
        as_of: lastSeen,
      },
      provider_name: EdrConfigurationType.MicrosoftEntraId,
    },
    fetchPolicy: 'cache-first',
  });

  const fetchEntraHistory = useCallback((offset: number, size: number) => {
    setEntraHistoryPagination({ offset, size });
  }, []);

  const entraSigninHistoryItems = useMemo(
    () =>
      (entraSigninHistoryData?.getUserSigninHistory?.items || []).filter(
        (item): item is EntraSigninWithUser => item != null,
      ),
    [entraSigninHistoryData],
  );

  const entraSigninHistoryTotal =
    entraSigninHistoryData?.getUserSigninHistory?.total_items || 0;

  // Crowdstrike User History Pagination state
  const [crowdstrikeHistoryPagination, setCrowdstrikeHistoryPagination] =
    useState({
      offset: 0,
      size: 50,
    });

  // Get detailed user history with identity_user field
  const {
    data: crowdstrikeUserHistoryData,
    loading: crowdstrikeUserHistoryLoading,
    error: crowdstrikeUserHistoryError,
    refetch: refetchCrowdstrikeUserHistory,
  } = useGetUserHistoryForEndpointDeviceEntityQuery({
    skip:
      !isModalOpen || !resolvedTenantId || !crowdstrikeEntityId || !lastSeen,
    variables: {
      tenant: resolvedTenantId,
      as_of: lastSeen,
      entity: {
        entity_id: crowdstrikeEntityId,
        entity_type: EntityDataStoreEntityType.EndpointDevice,
      },
      offset: crowdstrikeHistoryPagination.offset,
      size: crowdstrikeHistoryPagination.size,
      sort: [{ sort_by: 'obsts', sort_dir: 'desc' }],
      use_cache: true,
    },
    fetchPolicy: 'cache-first',
  });

  const fetchCrowdstrikeHistory = useCallback(
    (offset: number, size: number) => {
      setCrowdstrikeHistoryPagination({ offset, size });
    },
    [],
  );

  const crowdstrikeUserHistoryItems = useMemo(
    () =>
      (
        crowdstrikeUserHistoryData?.getUserHistoryForEndpointDeviceEntity
          ?.user_histories || []
      ).filter((item): item is NonNullable<typeof item> => item != null),
    [crowdstrikeUserHistoryData],
  );

  const crowdstrikeUserHistoryTotal =
    crowdstrikeUserHistoryData?.getUserHistoryForEndpointDeviceEntity
      ?.total_items || 0;

  const userIds = useMemo(() => {
    const userHistories =
      crowdstrikeUserHistoryData?.getUserHistoryForEndpointDeviceEntity
        ?.user_histories;

    if (!userHistories) return [];

    const extractedUserId = userHistories
      .map((u) => u?.identity_user?.user_id)
      .filter((id): id is string => !!id);
    return Array.from(new Set(extractedUserId));
  }, [crowdstrikeUserHistoryData]);

  // Fetch identity alerts for each user serially using the hook
  useGetIncidentsForUserQuery({
    skip:
      !resolvedTenantId ||
      currentUserIdIndex < 0 ||
      currentUserIdIndex >= userIds.length,
    variables: {
      tenant: resolvedTenantId,
      userId: userIds[currentUserIdIndex],
      provider: 'crowdstrike_identity' as EdrConfigurationType,
    },
    fetchPolicy: 'cache-first',
    onCompleted: (response) => {
      const incidents = (response.getIdentityIncidentsByUser || []).filter(
        (inc): inc is IdentityIncident => !!inc,
      );

      setIdentityAlerts((prev) => {
        const otherAlerts = prev.filter(
          (a) => a.userId !== userIds[currentUserIdIndex],
        );
        const newAlerts = incidents.map((incident) => ({
          ...incident,
          userId: userIds[currentUserIdIndex],
        }));
        return [...otherAlerts, ...newAlerts];
      });

      if (currentUserIdIndex < userIds.length - 1) {
        setCurrentUserIdIndex((prev) => prev + 1);
      } else {
        setIdentityAlertsLoading(false);
      }
    },
    onError: (error) => {
      setIdentityAlertsError(error);
      setIdentityAlertsLoading(false);
    },
  });

  useEffect(() => {
    if (userIds.length > 0) {
      setIdentityAlerts([]);
      setIdentityAlertsLoading(true);
      setIdentityAlertsError(undefined);
      setCurrentUserIdIndex(0);
    } else {
      setIdentityAlerts([]);
      setIdentityAlertsLoading(false);
      setCurrentUserIdIndex(-1);
    }
  }, [userIds]);

  const [performEdrActionMutation, { loading: performEdrActionLoading }] =
    usePerformEdrAction({
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast({
          status: 'success',
          title: edrAction.toasts.success,
          description: edrAction.toasts.statusUpdateSubmitted,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: edrAction.toasts.error,
          description: edrAction.toasts.statusUpdateFailed,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const performEdrAction = useCallback(
    async (params: PerformEdrActionParams) => {
      const { entityId, action, tenant, providerName, detectionId } = params;

      const refetchEntityId = entityId;

      await performEdrActionMutation({
        variables: {
          tenant,
          action,
          edr_device_input: {
            entity_id: entityId,
            detection_id: detectionId,
          },
          ...(providerName && { provider_name: providerName }),
        },
        refetchQueries: [
          {
            query: GET_ENTITY_EDR_STATUS,
            variables: {
              entity_id: refetchEntityId,
              tenant,
            },
          },
        ],
      });
    },
    [performEdrActionMutation],
  );

  const value = useMemo<EntityEdrContextType>(
    () => ({
      edrData: organizedData,
      resolvedTenantId,
      loading: edrLoading,
      error: edrError,
      hasAnyEDRIntegrationEnabled,

      crowdstrikeStatus: {
        data: crowdstrikeStatusData?.getEntityEdrStatus,
        loading: crowdstrikeStatusLoading,
        error: crowdstrikeStatusError,
        refresh: refreshCrowdstrikeStatus,
        refreshLoading: pullCrowdstrikeStatusLoading,
      },

      defenderStatus: {
        data: defenderStatusData?.getEntityEdrStatus,
        loading: defenderStatusLoading,
        error: defenderStatusError,
        refresh: refreshDefenderStatus,
        refreshLoading: pullDefenderStatusLoading,
      },

      paloAltoStatus: {
        data: paloAltoStatusData?.getEntityEdrStatus,
        loading: paloAltoStatusLoading,
        error: paloAltoStatusError,
        refresh: refreshPaloAltoStatus,
        refreshLoading: false, // Palo Alto doesn't have pull status
      },

      entraSigninHistory: {
        data: entraSigninHistoryItems,
        totalItems: entraSigninHistoryTotal,
        loading: entraSigninHistoryLoading || edrLoading,
        error: entraSigninHistoryError || edrError,
        fetchHistory: fetchEntraHistory,
        refetch: refetchEntraSigninHistory,
      },

      crowdstrikeUserHistory: {
        data: crowdstrikeUserHistoryItems,
        totalItems: crowdstrikeUserHistoryTotal,
        loading: crowdstrikeUserHistoryLoading || edrLoading,
        error: crowdstrikeUserHistoryError || edrError,
        fetchHistory: fetchCrowdstrikeHistory,
        refetch: refetchCrowdstrikeUserHistory,
      },

      identityAlerts: {
        data: identityAlerts,
        loading: identityAlertsLoading || crowdstrikeUserHistoryLoading,
        error: identityAlertsError || crowdstrikeUserHistoryError,
      },

      // Computed isolation status
      isIsolated:
        crowdstrikeStatusData?.getEntityEdrStatus?.entity_edr_status ===
          'isolated' ||
        crowdstrikeStatusData?.getEntityEdrStatus?.entity_edr_status ===
          'isolation_pending' ||
        defenderStatusData?.getEntityEdrStatus?.entity_edr_status ===
          'isolated' ||
        defenderStatusData?.getEntityEdrStatus?.entity_edr_status ===
          'isolation_pending',

      performEdrAction,
      performEdrActionLoading,
    }),
    [
      organizedData,
      edrLoading,
      edrError,
      hasAnyEDRIntegrationEnabled,
      crowdstrikeStatusData,
      crowdstrikeStatusLoading,
      crowdstrikeStatusError,
      refreshCrowdstrikeStatus,
      pullCrowdstrikeStatusLoading,
      defenderStatusData,
      defenderStatusLoading,
      defenderStatusError,
      refreshDefenderStatus,
      pullDefenderStatusLoading,
      paloAltoStatusData,
      paloAltoStatusLoading,
      paloAltoStatusError,
      refreshPaloAltoStatus,
      entraSigninHistoryItems,
      entraSigninHistoryTotal,
      entraSigninHistoryLoading,
      entraSigninHistoryError,
      fetchEntraHistory,
      refetchEntraSigninHistory,
      crowdstrikeUserHistoryItems,
      crowdstrikeUserHistoryTotal,
      crowdstrikeUserHistoryLoading,
      crowdstrikeUserHistoryError,
      fetchCrowdstrikeHistory,
      refetchCrowdstrikeUserHistory,
      resolvedTenantId,
      identityAlerts,
      identityAlertsLoading,
      identityAlertsError,
      performEdrAction,
      performEdrActionLoading,
    ],
  );

  return (
    <EntityEdrContext.Provider value={value}>
      {children}
    </EntityEdrContext.Provider>
  );
};

export const useEntityEdrContext = (): EntityEdrContextType => {
  const context = useContext(EntityEdrContext);
  if (!context) {
    throw new Error(
      'useEntityEdrContext must be used within an EntityEdrProvider',
    );
  }
  return context;
};

export const useEntityEdrContextSafe = (): EntityEdrContextType | undefined => {
  return useContext(EntityEdrContext);
};
